import React, { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle, useLayoutEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Slide from '@mui/material/Slide';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Panel from '../UI/Panel/Panel';
import Aux from '../../hoc/Auxilary/Auxilary';
import Grid from '@mui/material/Grid';
import TextField from '../UI/TextField/TextField';
import Button from '../UI/Buttons/Button';
import ActionBar from '../UI/Buttons/ActionBar';
import { useTranslation } from 'react-i18next';
import BreadcrumbBar from '../Navigation/Breadcrumbs/Breadcrumb';
import HeadersFootersForm from './HeadersFootersForm';

const HeadersFootersList = forwardRef(({ embedded = false, onActionBarChange }, ref) => {
    const auth = useSelector(state => state.auth);
    const permission = useSelector(state => state.security?.currentPath);
    const [list, setList] = useState([]);
    const [name, setName] = useState('');
    const [security, setSecurity] = useState(null);
    const { t } = useTranslation();

    // View mode: 'list' or 'edit'
    const [viewMode, setViewMode] = useState('list');
    const [editingId, setEditingId] = useState(null);
    const [initialLoad, setInitialLoad] = useState(true);

    const loadRecords = useCallback(async () => {
        try {
            const query = { name };
            const result = await axios.get('/api/headerandfooter/list', { params: query });
            setList(result.data || []);
        } catch (err) {
            console.error('Error loading headers and footers:', err);
            setList([]);
        }
    }, [name]);

    // Initial load
    useEffect(() => {
        if (initialLoad) {
            setInitialLoad(false);
            loadRecords();
        }
    }, [initialLoad, loadRecords]);

    // Update security when permission changes
    useEffect(() => {
        setSecurity(permission);
    }, [permission]);

    // Reload when name filter changes (after initial load)
    useEffect(() => {
        if (!initialLoad) {
            loadRecords();
        }
    }, [name, loadRecords, initialLoad]);

    const changeValue = (e) => {
        setName(e.target.value);
    };

    const handleCreate = useCallback(() => {
        setEditingId(null);
        setViewMode('edit');
    }, []);

    // Expose handleCreate to parent via ref
    useImperativeHandle(ref, () => ({
        handleCreate,
        canCreate: security?.create
    }), [handleCreate, security?.create]);

    // Update parent ActionBar when embedded
    useLayoutEffect(() => {
        if (embedded && onActionBarChange) {
            onActionBarChange({
                createClick: handleCreate,
                createAccess: security?.create,
                printViewClick: undefined,
                printViewTitle: undefined,
                enablePrintView: false
            });
        }
    }, [embedded, onActionBarChange, handleCreate, security?.create]);

    const handleEdit = (id) => {
        setEditingId(id);
        setViewMode('edit');
    };

    const handleBack = () => {
        setViewMode('list');
        setEditingId(null);
    };

    const handleFormSave = () => {
        loadRecords();
        handleBack();
    };

    const handleFormDelete = () => {
        loadRecords();
        handleBack();
    };

    const crumbs = useMemo(() => [
        { path: 'active', label: t('headersAndFooters'), screen: auth.screenDefs.HeadersAndFooters }
    ], [t, auth.screenDefs]);

    // Shared styles for table cells
    const headerCellSx = {
        color: 'primary.contrastText',
        fontWeight: 600,
        fontSize: '0.875rem',
        py: 2
    };

    const bodyCellSx = {
        fontWeight: 500,
        fontSize: '0.9375rem',
        py: 2
    };

    // Edit View
    const editView = (
        <Slide direction="left" in={viewMode === 'edit'} mountOnEnter unmountOnExit>
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <IconButton onClick={handleBack} sx={{ mr: 1 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6">
                        {editingId ? t('editHeaderFooter') : t('createHeaderFooter')}
                    </Typography>
                </Box>

                <HeadersFootersForm
                    recordId={editingId}
                    onSave={handleFormSave}
                    onCancel={handleBack}
                    onDelete={handleFormDelete}
                />
            </Box>
        </Slide>
    );

    // List View
    const listView = (
        <Slide direction="right" in={viewMode === 'list'} mountOnEnter unmountOnExit>
            <Box>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6">{t('headersAndFooters')}</Typography>
                </Box>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            value={name}
                            onChange={changeValue}
                            name="name"
                            size="small"
                            fullWidth={true}
                            label={t('name')}
                            data-cy='name'
                        />
                    </Grid>
                </Grid>

                <TableContainer component={Paper} sx={{
                    boxShadow: 2,
                    borderRadius: 2,
                    overflow: 'hidden'
                }}>
                    <Table aria-label="headersList">
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'primary.main' }}>
                                <TableCell sx={headerCellSx}>{t('name')}</TableCell>
                                <TableCell sx={{ ...headerCellSx, textAlign: 'center' }}>{t('global')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {list.map((row, i) =>
                                <TableRow
                                    key={row._id}
                                    onClick={() => handleEdit(row._id)}
                                    sx={{
                                        '&:hover': {
                                            bgcolor: 'action.hover',
                                            cursor: 'pointer'
                                        },
                                        '&:nth-of-type(odd)': {
                                            bgcolor: 'action.selected'
                                        }
                                    }}
                                >
                                    <TableCell sx={bodyCellSx} data-cy={'row' + i}>
                                        {row.name}
                                    </TableCell>
                                    <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
                                        {row.global ? t('yes') : t('no')}
                                    </TableCell>
                                </TableRow>
                            )}
                            {list.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">{t('noRecords')}</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Slide>
    );

    // Embedded mode: render the slide views directly
    if (embedded) {
        return (
            <Box>
                {viewMode === 'list' && listView}
                {viewMode === 'edit' && editView}
            </Box>
        );
    }

    // Standalone mode: render with full page chrome
    return (
        <Aux>
            <BreadcrumbBar crumbs={crumbs}>
                <ActionBar
                    createClick={handleCreate}
                    createAccess={security?.create}
                />
            </BreadcrumbBar>
            <Panel>
                {viewMode === 'list' && listView}
                {viewMode === 'edit' && editView}
            </Panel>
        </Aux>
    );
});

export default HeadersFootersList;
